import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  ArrowRight,
  Sparkle,
  Trophy,
  Timer,
  ThumbsUp,
  UsersThree,
  Warning,
  Lightbulb,
  Notebook,
  ListChecks,
  Flame,
  Briefcase,
  ChatCircleDots,
  ArrowUp,
  PaperPlaneTilt,
  ArrowClockwise,
} from "@phosphor-icons/react";
import { localDateStr } from "@/lib/date";
import { InstallBanner } from "@/components/InstallPWA";

const kindMeta = {
  conversation: { label: "Conversation", cls: "k-conversation" },
  plan: { label: "Plan", cls: "k-plan" },
  process: { label: "Task", cls: "k-process" },
  interview: { label: "Interview", cls: "k-interview" },
  opportunity: { label: "Opportunity", cls: "k-opportunity" },
  wisdom: { label: "Lesson", cls: "k-wisdom" },
  win: { label: "Win", cls: "k-win" },
};

const TONE_STYLES = {
  up: "border-teal-200 bg-teal-50/60 text-teal-800",
  down: "border-rose-200 bg-rose-50/60 text-rose-800",
  warn: "border-amber-200 bg-amber-50/70 text-amber-800",
  info: "border-slate-200 bg-slate-50/60 text-slate-700",
};

const TONE_ICON = {
  up: ArrowUp,
  down: Warning,
  warn: Warning,
  info: Sparkle,
};

function greeting(name) {
  const h = new Date().getHours();
  const stem = h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening";
  const first = (name || "").split(" ")[0];
  return first ? `${stem}, ${first}` : stem;
}

function formatHours(mins) {
  if (!mins) return "0h";
  const h = mins / 60;
  return h >= 10 ? `${Math.round(h)}h` : `${h.toFixed(1).replace(".0", "")}h`;
}

function relativeTime(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const s = Math.floor((Date.now() - then) / 1000);
  if (s < 60) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

/** Compact stat pill for the top row. */
function StatPill({ label, value, sub, icon: Icon, tone = "info", testid, to }) {
  const body = (
    <div className="bg-white rounded-2xl border border-slate-200/70 p-5 hover:border-teal-300 hover:shadow-[0_10px_30px_-16px_rgba(2,6,23,0.12)] transition-all h-full">
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-wider text-slate-500">{label}</div>
        <Icon size={18} weight="duotone" className="text-slate-400" />
      </div>
      <div className="mt-2 flex items-baseline gap-2">
        <div className="text-3xl font-semibold tracking-tight">{value}</div>
        {sub && (
          <div className={`text-xs font-medium ${tone === "down" ? "text-rose-600" : tone === "up" ? "text-teal-600" : "text-slate-500"}`}>{sub}</div>
        )}
      </div>
    </div>
  );
  return to ? (
    <Link data-testid={testid} to={to} className="focus:outline-none focus:ring-2 focus:ring-teal-400 rounded-2xl">{body}</Link>
  ) : (
    <div data-testid={testid}>{body}</div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [insights, setInsights] = useState(null);
  const [ask, setAsk] = useState("");
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/insights");
      setInsights(data);
      return data;
    } catch { /* keep last-known state on transient errors */ }
    return null;
  }, []);

  const refreshAi = useCallback(async () => {
    setRefreshing(true);
    try {
      await api.post("/insights/refresh-ai");
      await load();
    } catch { /* fallback narrative still shows below */ } finally {
      setRefreshing(false);
    }
  }, [load]);

  useEffect(() => { load(); }, [load]);

  // First-visit auto-generate: if the account has enough entries but no AI
  // narrative has ever been produced (scheduler hasn't run yet), trigger a
  // background refresh so the user doesn't have to hunt for the button.
  // Skip if we already generated in the last hour to avoid retry storms
  // after an LLM failure or an intentionally-empty narrative.
  const autoRefreshedRef = useRef(false);
  useEffect(() => {
    if (!insights || autoRefreshedRef.current || refreshing) return;
    const totalSubs = Object.values(insights.kind_counts || {}).reduce((a, b) => a + b, 0);
    const hasAiBullets = (insights.ai_narrative?.bullets?.length ?? 0) > 0;
    const genAt = insights.ai_narrative?.generated_at;
    const generatedRecently = genAt && (Date.now() - new Date(genAt).getTime()) < 60 * 60 * 1000;
    if (!hasAiBullets && !generatedRecently && totalSubs >= 5) {
      autoRefreshedRef.current = true;
      refreshAi();
    }
  }, [insights, refreshing, refreshAi]);

  const today = localDateStr();
  const wow = insights?.week?.wow_delta_pct;
  const wowSub = wow == null ? null : `${wow >= 0 ? "▲" : "▼"} ${Math.abs(wow)}% vs last`;
  const wowTone = wow == null ? "info" : wow >= 0 ? "up" : "down";

  const winsThisWeek = useMemo(() => {
    if (!insights) return 0;
    const monday = (() => { const d = new Date(); d.setDate(d.getDate() - ((d.getDay() + 6) % 7)); return localDateStr(d); })();
    return (insights.wins || []).filter((w) => w.date >= monday).length;
  }, [insights]);

  const submitAsk = (e) => {
    e.preventDefault();
    const q = ask.trim();
    if (!q) return navigate("/assistant");
    navigate(`/assistant?ask=${encodeURIComponent(q)}`);
  };

  return (
    <div data-testid="dashboard-page" className="space-y-8">
      {/* ---- Hero greeting ---- */}
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Executive Overview</div>
          <h1 className="text-3xl md:text-5xl font-semibold tracking-tight mt-1">
            {greeting(user?.name)}.
          </h1>
          <p className="text-sm md:text-base text-slate-500 mt-2 max-w-xl">
            Here's what AL-AXE has learned about your operation.
          </p>
        </div>
        <Link
          to={`/log?date=${today}`}
          data-testid="dashboard-open-log"
          className="inline-flex items-center gap-2 al-gradient text-white px-5 py-3 rounded-full font-medium active:scale-95 transition-transform"
        >
          Open today's log <ArrowRight size={16} weight="bold" />
        </Link>
      </header>

      {/* Install AL-AXE (PWA) — dismissible; only renders when installable */}
      <InstallBanner />

      {/* ---- What AL-AXE noticed (AI-derived patterns, cached, refreshable) ---- */}
      <section
        data-testid="insight-narrative"
        className="rounded-2xl al-gradient-border bg-white p-6 md:p-7"
      >
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl al-gradient text-white grid place-items-center shrink-0">
              <Sparkle size={20} weight="fill" />
            </div>
            <div className="min-w-0">
              <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
                {insights?.ai_narrative?.bullets?.length ? "AI-derived patterns" : "Auto-generated"}
              </div>
              <h2 className="text-lg font-semibold tracking-tight">What AL-AXE noticed</h2>
              {insights?.ai_narrative?.generated_at && (
                <div data-testid="ai-generated-at" className="text-xs text-slate-500 mt-0.5">
                  Generated {relativeTime(insights.ai_narrative.generated_at)}
                </div>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={refreshAi}
            disabled={refreshing}
            data-testid="refresh-ai-insights"
            title="Re-analyze the last 60 days"
            className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-600 hover:text-teal-700 disabled:text-slate-400 disabled:cursor-not-allowed border border-slate-200 rounded-full px-3 py-1.5 transition-colors"
          >
            <ArrowClockwise size={13} weight="bold" className={refreshing ? "animate-spin" : ""} />
            {refreshing ? "Analyzing…" : "Refresh"}
          </button>
        </div>

        {!insights && (
          <div className="text-sm text-slate-400 py-4">Reading your journal…</div>
        )}
        {insights?.ai_narrative?.not_enough_data && (
          <div className="text-sm text-slate-500 py-2">
            Log a few more entries and AL-AXE will start spotting patterns here. Meanwhile, here's what we can already see:
          </div>
        )}
        {insights?.ai_narrative?.error && (
          <div className="text-sm text-rose-600 py-2">Analysis failed — try Refresh in a moment.</div>
        )}

        {insights && (insights.narrative?.length ?? 0) === 0 && !insights.ai_narrative?.not_enough_data && (
          <div className="text-sm text-slate-400 py-4">Add a few entries and AL-AXE will start surfacing patterns here.</div>
        )}

        {insights && insights.narrative?.length > 0 && (
          <ul className="grid md:grid-cols-2 gap-2.5" data-testid="narrative-list">
            {insights.narrative.map((n, i) => {
              const Icon = TONE_ICON[n.tone] || Sparkle;
              return (
                <li
                  key={`${n.tone}-${n.text}`}
                  data-testid={`narrative-item-${i}`}
                  className={`flex items-start gap-2.5 rounded-xl border px-3.5 py-2.5 text-sm leading-snug ${TONE_STYLES[n.tone] || TONE_STYLES.info}`}
                >
                  <Icon size={16} weight="fill" className="mt-0.5 shrink-0 opacity-70" />
                  <div className="min-w-0 flex-1">
                    <div>{n.text}</div>
                    {n.sources?.length > 0 && (
                      <div className="mt-1.5 flex flex-wrap gap-1">
                        {n.sources.map((s, si) => (
                          <Link
                            key={`${s.log_date}-${s.subentry_id || si}`}
                            to={`/log?date=${s.log_date}${s.subentry_id ? `&sub=${s.subentry_id}` : ""}`}
                            data-testid={`narrative-source-${i}-${si}`}
                            className="text-[10px] font-medium bg-white/70 hover:bg-white text-current border border-current/20 rounded-full px-2 py-0.5 transition-colors"
                          >
                            {s.log_date}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* ---- Quick stats ---- */}
      <section className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatPill
          label="Entries this week"
          value={insights?.week?.entries_this_week ?? 0}
          sub={wowSub}
          tone={wowTone}
          icon={Notebook}
          testid="stat-entries"
          to="/log"
        />
        <StatPill
          label="Journaling streak"
          value={`${insights?.week?.streak ?? 0}d`}
          sub={insights?.week?.streak > 0 ? "keep it going" : "start today"}
          tone={insights?.week?.streak > 0 ? "up" : "info"}
          icon={Flame}
          testid="stat-streak"
          to={`/log?date=${today}`}
        />
        <StatPill
          label="Wins this week"
          value={winsThisWeek}
          sub={insights?.wins?.length ? `${insights.wins.length} in view` : null}
          tone="up"
          icon={Trophy}
          testid="stat-wins"
          to="/log"
        />
        <StatPill
          label="Open follow-ups"
          value={insights?.followups?.total_open ?? 0}
          sub={insights?.followups?.overdue?.length ? `${insights.followups.overdue.length} overdue` : "all on track"}
          tone={insights?.followups?.overdue?.length ? "down" : "up"}
          icon={ListChecks}
          testid="stat-followups"
          to="/followups"
        />
      </section>

      {/* ---- Wins + Task performance ---- */}
      <section className="grid lg:grid-cols-2 gap-5">
        {/* Wins */}
        <div className="bg-white rounded-2xl border border-slate-200/70 p-6" data-testid="wins-card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold inline-flex items-center gap-2">
              <Trophy size={18} weight="duotone" className="text-amber-500" /> Recent wins
            </h2>
            <Link to="/log" className="text-sm text-slate-500 hover:text-slate-900">Log a win →</Link>
          </div>
          {(!insights?.wins || insights.wins.length === 0) && (
            <div className="text-sm text-slate-400 py-6 text-center border border-dashed border-slate-200 rounded-xl">
              No wins captured yet. Every meaningful outcome counts.
            </div>
          )}
          <ul className="space-y-3">
            {insights?.wins?.slice(0, 3).map((w) => (
              <li key={`${w.date}-${w.subject}`} className="rounded-xl bg-amber-50/60 border border-amber-100 p-3.5">
                <div className="text-xs text-amber-700 font-medium">{w.date}</div>
                <div className="text-sm font-medium mt-0.5">{w.subject || "Untitled win"}</div>
                {w.people?.length > 0 && (
                  <div className="text-xs text-slate-500 mt-1">With {w.people.join(", ")}</div>
                )}
                {w.notes && <div className="text-xs text-slate-600 mt-1.5 line-clamp-2">{w.notes}</div>}
              </li>
            ))}
          </ul>
        </div>

        {/* Task performance */}
        <div className="bg-white rounded-2xl border border-slate-200/70 p-6" data-testid="tasks-card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold inline-flex items-center gap-2">
              <Timer size={18} weight="duotone" className="text-teal-600" /> Task performance
            </h2>
            <Link to="/reports" className="text-sm text-slate-500 hover:text-slate-900">Reports →</Link>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-xl bg-slate-50 p-4">
              <div className="text-xs uppercase tracking-wider text-slate-500">Time logged</div>
              <div className="mt-1 text-2xl font-semibold">{formatHours(insights?.tasks?.total_minutes)}</div>
              <div className="text-[11px] text-slate-500">last 90 days</div>
            </div>
            <div className="rounded-xl bg-slate-50 p-4">
              <div className="text-xs uppercase tracking-wider text-slate-500 inline-flex items-center gap-1">
                <ThumbsUp size={12} weight="fill" className="text-teal-600" /> Good outcomes
              </div>
              <div className="mt-1 text-2xl font-semibold">{insights?.tasks?.thumbs_up_pct ?? "—"}{insights?.tasks?.thumbs_up_pct != null ? "%" : ""}</div>
              <div className="text-[11px] text-slate-500">
                {insights?.tasks?.outcomes?.up ?? 0} good · {insights?.tasks?.outcomes?.down ?? 0} bad
              </div>
            </div>
          </div>
          {insights?.tasks?.top_types?.length > 0 && (
            <div className="mt-4">
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Top task types</div>
              <div className="flex flex-wrap gap-2">
                {insights.tasks.top_types.map((t) => (
                  <span key={t.name} className="text-xs px-2.5 py-1 rounded-full bg-slate-100 text-slate-700 font-medium">
                    {t.name} <span className="text-slate-400">· {t.count}</span>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ---- People pulse + follow-ups ---- */}
      <section className="grid lg:grid-cols-2 gap-5">
        <div className="bg-white rounded-2xl border border-slate-200/70 p-6" data-testid="people-card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold inline-flex items-center gap-2">
              <UsersThree size={18} weight="duotone" className="text-indigo-500" /> People pulse
            </h2>
            <Link to="/people" className="text-sm text-slate-500 hover:text-slate-900">View all →</Link>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">Most engaged (90d)</div>
              {insights?.people?.top?.length ? (
                <ul className="space-y-1.5">
                  {insights.people.top.map((p) => (
                    <li key={p.name}>
                      <Link
                        to={`/people?name=${encodeURIComponent(p.name)}`}
                        className="text-sm text-slate-800 hover:text-teal-700 flex justify-between items-baseline"
                      >
                        <span className="truncate">{p.name}</span>
                        <span className="text-xs text-slate-400 ml-2">{p.count_90d}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="text-xs text-slate-400">No interactions yet.</div>
              )}
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">Might be stale</div>
              {insights?.people?.stale?.length ? (
                <ul className="space-y-1.5">
                  {insights.people.stale.map((p) => (
                    <li key={p.name}>
                      <Link
                        to={`/people?name=${encodeURIComponent(p.name)}`}
                        className="text-sm text-slate-800 hover:text-teal-700 flex justify-between items-baseline"
                      >
                        <span className="truncate">{p.name}</span>
                        <span className="text-xs text-amber-600 ml-2">{p.days}d</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="text-xs text-slate-400">Everyone is fresh — nice.</div>
              )}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200/70 p-6" data-testid="followups-card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold inline-flex items-center gap-2">
              <ListChecks size={18} weight="duotone" className="text-rose-500" /> Follow-ups
            </h2>
            <Link to="/followups" className="text-sm text-slate-500 hover:text-slate-900">Manage →</Link>
          </div>
          {(!insights?.followups?.overdue?.length && !insights?.followups?.upcoming?.length) ? (
            <div className="text-sm text-slate-400 py-4">All clear — no pending follow-ups.</div>
          ) : (
            <ul className="space-y-2">
              {insights?.followups?.overdue?.map((f) => (
                <li key={f.id} className="flex items-start gap-3 rounded-xl bg-rose-50/70 border border-rose-100 p-3">
                  <Warning size={16} weight="fill" className="text-rose-500 mt-0.5 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{f.title}</div>
                    <div className="text-xs text-rose-700 mt-0.5">{f.days_overdue}d overdue{f.person_name ? ` · ${f.person_name}` : ""}</div>
                  </div>
                </li>
              ))}
              {insights?.followups?.upcoming?.map((f) => (
                <li key={f.id} className="flex items-start gap-3 rounded-xl bg-slate-50 border border-slate-100 p-3">
                  <div className="w-2 h-2 mt-2 rounded-full al-gradient shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{f.title}</div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {f.due_date || "no date"}{f.person_name ? ` · ${f.person_name}` : ""}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      {/* ---- Lessons + Opportunities + Pipeline ---- */}
      <section className="grid lg:grid-cols-3 gap-5">
        <div className="bg-white rounded-2xl border border-slate-200/70 p-6" data-testid="lessons-card">
          <h3 className="text-sm font-semibold inline-flex items-center gap-2 mb-3">
            <Lightbulb size={16} weight="duotone" className="text-fuchsia-500" /> Recent lessons
          </h3>
          {(insights?.lessons || []).length === 0 ? (
            <div className="text-xs text-slate-400 py-4">No lessons captured yet.</div>
          ) : (
            <ul className="space-y-2.5">
              {insights.lessons.slice(0, 3).map((w) => (
                <li key={`${w.date}-${w.subject || w.notes}`} className="text-sm">
                  <div className="text-[11px] text-slate-500">{w.date}</div>
                  <div className="text-slate-800 line-clamp-2">{w.subject || w.notes}</div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="bg-white rounded-2xl border border-slate-200/70 p-6" data-testid="opps-card">
          <h3 className="text-sm font-semibold inline-flex items-center gap-2 mb-3">
            <Sparkle size={16} weight="duotone" className="text-teal-500" /> Recent opportunities
          </h3>
          {(insights?.opportunities || []).length === 0 ? (
            <div className="text-xs text-slate-400 py-4">Nothing on deck.</div>
          ) : (
            <ul className="space-y-2.5">
              {insights.opportunities.slice(0, 3).map((w) => (
                <li key={`${w.date}-${w.subject || w.notes}`} className="text-sm">
                  <div className="text-[11px] text-slate-500">{w.date}</div>
                  <div className="text-slate-800 line-clamp-2">{w.subject || w.notes}</div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="bg-white rounded-2xl border border-slate-200/70 p-6" data-testid="pipeline-card">
          <h3 className="text-sm font-semibold inline-flex items-center gap-2 mb-3">
            <Briefcase size={16} weight="duotone" className="text-slate-600" /> Interview pipeline
          </h3>
          {(insights?.upcoming_interviews || []).length === 0 ? (
            <div className="text-xs text-slate-400 py-4">No interviews scheduled ahead.</div>
          ) : (
            <ul className="space-y-2.5">
              {insights.upcoming_interviews.slice(0, 3).map((it) => (
                <li key={`${it.date}-${it.applicant}-${it.position}`} className="text-sm">
                  <div className="text-[11px] text-slate-500">{it.date}</div>
                  <div className="text-slate-800 truncate">{it.applicant || "Candidate"}{it.position ? ` · ${it.position}` : ""}</div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      {/* ---- Ask AL-AXE bar ---- */}
      <section
        data-testid="ask-bar"
        className="rounded-2xl al-gradient text-white p-5 md:p-6 shadow-md"
      >
        <div className="flex items-start gap-3 mb-4">
          <Sparkle size={22} weight="fill" className="shrink-0" />
          <div>
            <div className="text-xs uppercase tracking-widest opacity-80">Ask AL-AXE</div>
            <div className="text-base md:text-lg font-medium">Anything above got you curious?</div>
          </div>
        </div>
        <form onSubmit={submitAsk} className="flex flex-col sm:flex-row gap-2">
          <input
            data-testid="ask-input"
            value={ask}
            onChange={(e) => setAsk(e.target.value)}
            placeholder="e.g. Why was Tuesday my busiest day?"
            className="flex-1 min-w-0 rounded-full bg-white/95 text-slate-900 placeholder:text-slate-400 px-5 py-3 focus:outline-none focus:ring-2 focus:ring-white/60 text-sm"
          />
          <button
            type="submit"
            data-testid="ask-submit"
            className="bg-white text-slate-900 rounded-full px-5 py-3 font-medium inline-flex items-center justify-center gap-2 hover:shadow-md transition-all"
          >
            Ask <PaperPlaneTilt size={16} weight="fill" />
          </button>
        </form>
        <div className="mt-3 flex flex-wrap gap-2">
          {["Summarise this week", "What follow-ups are open?", "Which people am I overdue with?"].map((q) => (
            <button
              key={q}
              type="button"
              data-testid={`ask-suggestion-${q.slice(0, 8).replace(/\s+/g, "-").toLowerCase()}`}
              onClick={() => navigate(`/assistant?ask=${encodeURIComponent(q)}`)}
              className="text-xs bg-white/15 hover:bg-white/25 text-white px-3 py-1.5 rounded-full transition-colors"
            >
              <ChatCircleDots size={12} weight="fill" className="inline mr-1" /> {q}
            </button>
          ))}
        </div>
      </section>
    </div>
  );
}
